// Which clinic this dentist is currently working in.
//
// This is app-wide state, not screen state, for a reason the BFF forces:
// patientmanagement's ListPatients accepts a doctor_id and IGNORES it — only
// clinic_id and search actually filter — so an unscoped patient list is the
// whole platform's registry. The BFF therefore refuses an ambiguous request
// with CLINIC_REQUIRED, and this context is what supplies the answer.
//
// The membership list is read ONCE per session (it changes when a clinic
// manager accepts a join, not while the dentist works) and shared, rather than
// each screen firing its own myClinics.
//
// The choice is persisted locally only. There is no server-side "active clinic"
// — the doctor BFF's `x-active-clinic-id` header exists and NOTHING in the
// platform sends it, so its value there is the doctor's own sub: a clinic id
// that does not exist. This app does not repeat that.

import React from 'react';
import { bffQuery } from '../lib/bffClient.js';
import { authorizesPatients, clinicScopeState } from '../lib/patients.js';

const STORAGE_KEY = 'parsina.dentist.clinic';

// `clinic.currency` is what the clinic keeps its books in (ISO code, never blank;
// a clinic that predates the field reads IRR). It is how a money FIELD knows its
// unit before any plan exists — «خدمت جدید» parses a typed tariff in it. A
// document already on screen is read in its OWN currency, never this one.
export const MY_CLINICS_QUERY = `
  query MyClinics {
    myClinics {
      id
      clinicId
      status
      active
      visitDurationMinutes
      clinic { id name city province officePhone invitationCode currency }
    }
  }
`;

const ClinicScopeContext = React.createContext(null);

const readStored = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

export function ClinicScopeProvider({ children }) {
  const [clinics, setClinics] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [activeId, setActiveIdState] = React.useState(readStored);
  const [nonce, setNonce] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    bffQuery(MY_CLINICS_QUERY)
      .then((data) => {
        if (!alive) return;
        setClinics(data?.myClinics || []);
        setError(null);
      })
      .catch((err) => {
        if (!alive) return;
        // A membership read that fails leaves the list empty, and every screen
        // that needs a clinic says so. It must NOT fall back to "no clinic
        // scope" and fetch the platform's whole registry.
        console.error('[clinicScope] myClinics failed:', err?.message);
        setError(err);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [nonce]);

  const setActiveId = React.useCallback((id) => {
    setActiveIdState(id || null);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage off — the choice simply does not survive a reload */
    }
  }, []);

  // A stored clinic the dentist was removed from — or one that has not accepted
  // them yet — must not keep scoping queries. The rule itself lives in
  // lib/patients.js: it is pure, it is tested there, and it was previously
  // written out a second time here, so a change to one copy would have left the
  // hook and its own test disagreeing.
  const { state: scopeState, clinicId: resolvedActive } = clinicScopeState({ clinics, activeId });

  // The memberships that may actually scope a query — `clinics` still carries
  // every row, including pending ones, because پروفایل shows their status.
  // Anything that PICKS a scope (the sidebar switcher) has to offer this list:
  // an option that cannot scope is an option that produces CLINIC_FORBIDDEN.
  const scopeClinics = React.useMemo(() => clinics.filter(authorizesPatients), [clinics]);

  const value = React.useMemo(
    () => ({
      clinics,
      scopeClinics,
      scopeState,
      loading,
      error,
      activeId: resolvedActive,
      activeClinic: clinics.find((c) => c.clinicId === resolvedActive) || null,
      setActiveId,
      reload: () => setNonce((n) => n + 1),
    }),
    [clinics, scopeClinics, scopeState, loading, error, resolvedActive, setActiveId],
  );

  return <ClinicScopeContext.Provider value={value}>{children}</ClinicScopeContext.Provider>;
}

// Safe outside the provider (a test, an error boundary above it): reports "no
// clinics, still loading" rather than throwing. A missing provider must not be
// the reason a clinical screen fails to render.
export function useClinicScope() {
  return (
    React.useContext(ClinicScopeContext) ?? {
      clinics: [],
      scopeClinics: [],
      scopeState: 'none',
      loading: true,
      error: null,
      activeId: null,
      activeClinic: null,
      setActiveId: () => {},
      reload: () => {},
    }
  );
}
